import _ from 'lodash'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { IDB_posts } from '../IDB/IDB.dexie'
import { IDB_calendarEventsReadAll } from '../IDB/IDB.table.calendarEvent'
import { IDB_fileLookupsReadAll } from '../IDB/IDB.table.file'
import { IDB_formsReadAllIncDeleted } from '../IDB/IDB.table.form'
import { IDB_reportsReadAll } from '../IDB/IDB.table.report'
import { Post_reportPostToSyncPost } from '../Post/Post.TPost'
import { Rpts_ensureReportHasEvent } from './Rpts.ensureReportHasEvent'
import type { Rpts_ExhaustiveAnswer } from './Rpts.ExhaustiveAnswer'
import { Rpts_replaceImagesInReportedActions } from './Rpts.replaceImagesInReportedActions'
import type { NReports } from './Rpts.types'

function isUploadedReport(
    report: NReports.TDatabaseReport
): report is NReports.TSubmittedReport | NReports.TSyncReport {
    return report.uploaded === true
}

export type Rpts_PreparedReports = {
    reportToSend: NReports.TSubmittedReport
    reportToSave: NReports.INewIntermediateReport
}

export async function Rpts_prepareReportsForUpload(logger: ClientLogger): Promise<Rpts_PreparedReports[]> {
    logger.log(`preparing reports for upload`)

    const preparedReports: Rpts_PreparedReports[] = []

    const [reports, fileLookups, events, forms] = await Promise.all([
        IDB_reportsReadAll(),
        IDB_fileLookupsReadAll(),
        IDB_calendarEventsReadAll(),
        IDB_formsReadAllIncDeleted(),
    ])

    const replaceImage = (fileId: number): string => {
        const lookup = _.find(fileLookups, { fileId })
        return lookup == null ? '' : (lookup.fileName as string)
    }

    for (const report of reports) {
        const reportClientId = report.clientId
        try {
            if (isUploadedReport(report)) {
                logger.log(`report has already been uploaded`, { reportClientId })
                continue
            }

            Rpts_ensureReportHasEvent(report, events)

            // Avoid null references for old reports
            report.answers = report.answers || []

            // Replace pendingPhotos answers with the uploaded photos
            const answers = report.answers.map((answer): Rpts_ExhaustiveAnswer => {
                if (answer.value.type !== 'pendingPhotos') return answer

                return {
                    ...answer,
                    value: {
                        type: 'photos',
                        value: answer.value.value.map(replaceImage).filter(Boolean),
                    },
                }
            })

            const reportToSend: NReports.TSubmittedReport = {
                clientId: report.clientId,
                parentClientId: report.parentClientId,
                createPost: false,
                scheduledInstance: report.scheduledInstance,
                location: report.location,
                subjectUnitId: report.subjectUnitId,
                subjectUnitVersion: report.subjectUnitVersion,
                event: report.event?.onlineId,
                startDate: report.startDate,
                endDate: report.endDate,
                coordinates: report.coordinates,
                answers,
                teamDoneFor: report.teamDoneFor,
                workflowVersion: report.workflowVersion,
                flowUnitIds: report.flowUnitIds,
                reportedActionList: {
                    actionList: report.reportedActionList.actionList,
                    reportedActions: Rpts_replaceImagesInReportedActions(
                        report.reportedActionList.reportedActions,
                        forms,
                        replaceImage
                    ),
                },
            }

            // Optionally add report title
            if (report.title != null) {
                reportToSend.title = report.title
            }

            try {
                const post = await IDB_posts().where({ reportClientId: report.clientId }).first()

                if (post != null) {
                    reportToSend.post = Post_reportPostToSyncPost(post)
                    logger.log(`found report post`, { reportClientId, postId: post?.id })
                }
            } catch (error) {
                logger.error(error, { reportClientId })
            }

            if (reportToSend.post == null) {
                reportToSend.createPost = true
                logger.log(`failed to find report post`, { reportClientId })
            }

            // TODO we need to delete extra validations created for the transition period
            if (!reportToSend.event) {
                delete reportToSend.event
                logger.log(`missing calendar event`, { reportClientId })
            }

            preparedReports.push({ reportToSend, reportToSave: report })

            logger.log(`prepared report for upload`, { reportClientId })
        } catch (error) {
            logger.error(error, { reportClientId })
        }
    }

    logger.log(`prepared reports for upload`, { count: preparedReports.length, outOf: reports.length })
    return preparedReports
}
